import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Droite graduée SVG côté élève — lecture seule, thème dark, zoom possible
 */

const FractionDisplay = ({ numerator, denominator, className = "" }) => (
    <span className={`inline-flex flex-col items-center leading-none ${className}`}>
        <span className="border-b border-current px-0.5">{numerator}</span>
        <span className="px-0.5">{denominator}</span>
    </span>
);

const NumberLineElementEleve = ({ config, theme }) => {
    const [isZoomed, setIsZoomed] = useState(false);

    // Téléphone tenu en portrait : en zoom, la droite est tournée de 90°
    // pour prendre la hauteur de l'écran comme largeur (~2× plus grand).
    // Tourner physiquement le téléphone (paysage) rebascule à plat.
    const [portraitEtroit, setPortraitEtroit] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(orientation: portrait) and (max-width: 640px)');
        const maj = () => setPortraitEtroit(mq.matches);
        maj();
        mq.addEventListener('change', maj);
        return () => mq.removeEventListener('change', maj);
    }, []);

    // Bloquer le scroll du body quand le zoom est ouvert
    useEffect(() => {
        if (!isZoomed) return;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, [isZoomed]);

    const defaultConfig = {
        start: 0, end: 10, step: 1,
        lineType: 'half', showArrow: true,
        showStart: true, showEnd: true,
        label: '', points: [],
    };

    const c = { ...defaultConfig, ...config };
    const MAX_GRADUATIONS = 101;

    const getGraduations = () => {
        const { start, end, step } = c;
        if (step <= 0 || start >= end) return [];
        const count = Math.round((end - start) / step) + 1;
        const effectiveCount = Math.min(count, MAX_GRADUATIONS);
        const effectiveStep = (end - start) / (effectiveCount - 1);
        const grads = [];
        for (let k = 0; k < effectiveCount; k++) {
            const val = start + k * effectiveStep;
            grads.push(Math.round(val * 1e9) / 1e9);
        }
        return grads;
    };

    const graduations = getGraduations();
    const getPoints = () => Array.isArray(c.points) ? c.points : [];
    const findPoint = (value) => getPoints().find(p => Math.abs(p.value - value) < 0.001);
    const reperesArr = Array.isArray(c.reperes) ? c.reperes : [];
    const reperesSet = new Set(reperesArr.map(v => Math.round(v * 1000)));

    // ─── SVG dimensions ───
    const { start, end, showArrow, showStart, showEnd, lineType } = c;
    const isFullLine = lineType === 'full';
    const W = 600, H = 90, lineY = 40;
    const padL = isFullLine ? 40 : 25;
    const padR = showArrow ? 35 : 15;
    const usableW = W - padL - padR;
    const toX = (val) => padL + ((val - start) / (end - start)) * usableW;
    const tickSmall = 4, tickBig = 6;
    // Le rond d'un point : assez gros pour se voir sans étiquette, plus large
    // qu'une graduation, pour qu'on ne le confonde pas avec elle.
    const RAYON_POINT = 4;

    // Couleurs adaptées au thème ; la modale de zoom force une palette
    // claire (fond slate-950 : les couleurs du thème light y sont illisibles)
    const isLight = theme === 'light';
    const themeColors = {
        line: isLight ? '#475569' : '#94a3b8',  // slate-600 / slate-400
        text: isLight ? '#1e293b' : '#cbd5e1',  // slate-800 / slate-300
        frac: isLight ? 'text-gray-800' : 'text-slate-300',
    };
    const zoomColors = { line: '#e2e8f0', text: '#f8fafc', frac: 'text-slate-100' };

    const formatValue = (val) => {
        if (Number.isInteger(val)) return String(val);
        return Number(val.toFixed(4)).toString().replace('.', ',');
    };

    // grand : zoom portrait tourné — la rotation ne grossit que d'environ
    // ×1,2, on agrandit donc les écritures dans le repère du SVG
    const renderSvgValue = (point, x, y, colors, grand) => {
        if (point?.displayFraction) {
            const w = grand ? 44 : 28;
            return (
                <foreignObject x={x - w / 2} y={y - 2} width={w} height={grand ? 34 : 24} key={`frac-${x}`}>
                    <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', justifyContent: 'center' }}>
                        <FractionDisplay
                            numerator={point.displayFraction.num}
                            denominator={point.displayFraction.den}
                            className={`${grand ? 'text-[15px]' : 'text-[10px]'} ${colors.frac}`}
                        />
                    </div>
                </foreignObject>
            );
        }
        const val = point?.value ?? point;
        return (
            <text x={x} y={y + (grand ? 14 : 9)} textAnchor="middle" fill={colors.text} className={grand ? 'text-[16px]' : 'text-[10px]'}>
                {formatValue(val)}
            </text>
        );
    };

    const buildSvg = (colors, grand = false) => (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
            {/* Extension gauche (droite complète) */}
            {isFullLine && (
                <line x1={padL - 18} y1={lineY} x2={padL} y2={lineY} stroke={colors.line} strokeWidth={1} />
            )}

            {/* Ligne principale */}
            <line x1={padL} y1={lineY} x2={W - padR + (showArrow ? 8 : 0)} y2={lineY} stroke={colors.line} strokeWidth={1} />

            {/* Flèche droite */}
            {showArrow && (
                <polygon
                    points={`${W - padR + 15},${lineY} ${W - padR + 6},${lineY - 4} ${W - padR + 6},${lineY + 4}`}
                    fill={colors.line}
                />
            )}

            {/* Graduations */}
            {graduations.map((value, index) => {
                const x = toX(value);
                const isFirst = index === 0;
                const isLast = index === graduations.length - 1;
                const point = findPoint(value);
                const isRepere = reperesSet.has(Math.round(value * 1000));
                const isMajor = isFirst || isLast || !!point || isRepere;
                const tH = isMajor ? tickBig : tickSmall;
                const color = point?.color || colors.line;

                const shouldShowNumber =
                    (isFirst && showStart) ||
                    (isLast && showEnd) ||
                    (point && point.showValue) ||
                    isRepere;

                return (
                    <g key={index}>
                        <line x1={x} y1={lineY - tH} x2={x} y2={lineY + tH} stroke={color} strokeWidth={1} />

                        {/* un point, c'est un rond : toujours de la même taille, étiquette ou pas */}
                        {point && <circle cx={x} cy={lineY} r={RAYON_POINT} fill={color} />}
                        {point?.label && (
                            <text x={x} y={lineY - tickBig - (grand ? 9 : 6)} textAnchor="middle" fill={color} className={`${grand ? 'text-[16px]' : 'text-[10px]'} font-bold`}>
                                {point.label}
                            </text>
                        )}

                        {shouldShowNumber && (
                            point
                                ? renderSvgValue(point, x, lineY + tH + 4, colors, grand)
                                : <text x={x} y={lineY + tH + (grand ? 20 : 14)} textAnchor="middle" fill={colors.text} className={grand ? 'text-[16px]' : 'text-[10px]'}>{formatValue(value)}</text>
                        )}
                    </g>
                );
            })}

            {/* Points hors graduations : un ROND, jamais un trait. Avant, un
                point entre deux graduations recevait un trait de la hauteur
                d'une grande graduation : l'élève voyait une graduation de
                plus, au mauvais endroit, et les parts égales ne se lisaient
                plus (constat prof 2026-09-12, fractions de gen_607 sur une
                droite en dixièmes). */}
            {getPoints()
                .filter(p => !graduations.some(g => Math.abs(g - p.value) < 0.001))
                .map((point, idx) => {
                    const relativePos = (point.value - start) / (end - start);
                    if (relativePos < 0 || relativePos > 1) return null;
                    const x = toX(point.value);

                    return (
                        <g key={`extra-${idx}`}>
                            <circle cx={x} cy={lineY} r={RAYON_POINT} fill={point.color} />
                            {point.label && (
                                <text x={x} y={lineY - tickBig - (grand ? 9 : 6)} textAnchor="middle" fill={point.color} className={`${grand ? 'text-[16px]' : 'text-[10px]'} font-bold`}>
                                    {point.label}
                                </text>
                            )}
                            {point.showValue && renderSvgValue(point, x, lineY + tickBig + 4, colors, grand)}
                        </g>
                    );
                })}
        </svg>
    );

    return (
        <>
            <div
                className="w-full my-1 cursor-zoom-in"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setIsZoomed(true); }}
            >
                {buildSvg(themeColors)}

                {c.label && (
                    <div className={`text-center text-xs italic ${isLight ? 'text-gray-600' : 'text-slate-400'}`}>{c.label}</div>
                )}
            </div>

            {/* Modal zoom plein écran (porté sur document.body pour sortir du transform de la carte) */}
            {isZoomed && createPortal(
                <div
                    className="fixed inset-0 z-[10020] bg-slate-950/95 backdrop-blur-md flex items-center justify-center overflow-hidden"
                    onClick={() => setIsZoomed(false)}
                >
                    <button
                        onClick={() => setIsZoomed(false)}
                        className="absolute top-4 right-4 w-10 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center text-white transition-colors z-10"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <div
                        className={`flex flex-col items-center justify-center ${portraitEtroit ? 'rotate-90 w-[88vh]' : 'w-[95vw] max-h-[90vh]'}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {buildSvg(zoomColors, portraitEtroit)}
                        {c.label && (
                            <div className="text-center mt-3 text-sm text-slate-200 italic">{c.label}</div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default NumberLineElementEleve;
